import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { InlineWidget, useCalendlyEventListener, EventScheduledEvent } from "react-calendly"
import { Tabs } from 'antd';
import { useUser } from '../../hooks/useUser';
import { useToast } from "../../hooks/useToast";
import styles from './officeHourModal.module.scss';
import Modal from '../modal/Modal';
import Spinner from '../spinner/Spinner';
import Button from '../button/Button';
import { CountDown } from './CountDown';
import { PulseDot } from '../pulse/Pulse';
import { useCalendarEventsLazyQuery } from '../../api/graphql-codegen';
import { isHomeEDApp } from '@constants';
import { logger } from '../../utils/logger';
const { TabPane } = Tabs;

const Calendar_Hayley_URL = 'https://calendly.com/openparachute-officehours/clinical-support?hide_event_type_details=1&hide_gdpr_banner=1';
const Calendar_URL = 'https://calendly.com/openparachute-officehours/onboarding?hide_event_type_details=1&hide_gdpr_banner=1';
const onboardingMeetingUrl = 'https://zoom.us/wc/join/9887223539?pwd=M0NFOU5lS3BKZzVXK1FZb0IzZ1VtUT09';
const clinicalMeetingUrl = 'https://zoom.us/wc/join/9887223539?pwd=M0NFOU5lS3BKZzVXK1FZb0IzZ1VtUT09';

const utm = {
  utmCampaign: "Open Parachute",
  utmContent: "Education",
  utmTerm: "Education",
};

const pageSettings = {
  backgroundColor: "ffffff",
  hideEventTypeDetails: true,
  hideLandingPageDetails: true,
  primaryColor: "00a2ff",
  textColor: "4d5055",
  hideGdprBanner: true,
};

type Props = {
  open: boolean;
  onClose: () => void;
};

enum TabKeys {
  Clinical = "Clinical",
  Onboarding = "Onboarding"
}

function OfficeHourModal(props: Props) {
  const {open, onClose} = props;
  const {successToast} = useToast();
  const [isLoading, setLoading] = useState(true);
  const [onboardingMeetingData, setOnboardingMeetingData] = useState<{ timeLeftInSec?: number; isInMeeting?: boolean; }>({timeLeftInSec: undefined, isInMeeting: false});
  const [clinicalMeetingData, setClinicalMeetingData] = useState<{ timeLeftInSec?: number; isInMeeting?: boolean; }>({timeLeftInSec: undefined, isInMeeting: false});
  const [activeTab, setActiveTab] = useState<TabKeys>(TabKeys.Clinical);
  const [user] = useUser();
  const timer = useRef<NodeJS.Timeout | null>(null);
  const [getCalendarEvent] = useCalendarEventsLazyQuery({ fetchPolicy: 'network-only' });
  const prefill = useMemo(() => {
    if (!user) return undefined;
    return {
      name: `${user.firstName || ''} ${user.lastName || ''}`,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      guests: [],
    };
  }, [user]);

  const onEventScheduled = useCallback((e: EventScheduledEvent) => {
    setLoading(false);
    setTimeout(() => {
      onClose();
      successToast('You have booked successfully Office Hour call!');
    }, 3000);
  }, [onClose, successToast]);

  const onEventTypeViewed = useCallback((e) => {
    setLoading(false);
  }, []);

  useCalendlyEventListener({
    onProfilePageViewed: () => logger.info("onProfilePageViewed"),
    onDateAndTimeSelected: () => logger.info("onDateAndTimeSelected"),
    onEventTypeViewed: onEventTypeViewed,
    onEventScheduled: onEventScheduled,
  });

  const getNextEvent = useCallback(async () => {
    const data = (await getCalendarEvent()).data?.calendarEvents;
    const nextClinical = data?.clinical
    const nextOnboarding = data?.onboarding
    if (nextClinical) {
      setClinicalMeetingData(nextClinical);
      if (nextClinical.isInMeeting) {
        setActiveTab(TabKeys.Clinical);
      }
    }
    if (nextOnboarding) {
      if (nextOnboarding.isInMeeting) {
        setActiveTab(TabKeys.Onboarding);
      }
      setOnboardingMeetingData(nextOnboarding);
    }
    const isInMeeting = !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
    if (isInMeeting) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      const timeLeft = Math.min(data?.clinical.timeLeftInSec || 0, data?.onboarding.timeLeftInSec || 0);
      timer.current = setTimeout(() => {
        getNextEvent();
      }, (timeLeft + 31 * 60) * 1000);
    }
  }, [getCalendarEvent]);

  useEffect(() => {
    if (isHomeEDApp) {
      return;
    }
    if (open && getNextEvent) {
      getNextEvent();
    } else {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    }
  }, [getNextEvent, open])

  const joinMeeting = useCallback((link: string) => () => {
    window.open(link, '_blank');
  }, [])

  const onChange = (key: string) => {
    setActiveTab(key as TabKeys);
  };

  const onCountDownHitZero = useCallback(() => {
    setTimeout(() => {
      getNextEvent();
    }, 1000);
  }, [getNextEvent]);

  return (
    <Modal open={open} onClose={onClose} className={styles.modalContainer} noPadding={true}>
      <div className={styles.upperContainer}>
        <div className={styles.title}>Welcome to Office Hours</div>
        <div className={styles.textsContainer}>
          <div className={styles.csText}>Join Open Parachute on a Zoom call to <span className={styles.csTextBold}>learn about the resources</span> or <span className={styles.csTextBold}>ask a question</span> about using the program. These Zoom calls are <span className={styles.csTextBold}>available to all Educators</span> who are using the Open Parachute resources.</div>
        </div>
      </div>
      <div className={styles.tabsContainer}>
        <Tabs defaultActiveKey={TabKeys.Clinical} activeKey={activeTab} onChange={onChange} size='large' className={styles.tabsWrapper}>
          <TabPane
            tab={<div className={`${styles.tabTitle} ${activeTab === TabKeys.Clinical ? styles.activeTab : ''}`}>Clinical Support {clinicalMeetingData.isInMeeting ? <PulseDot hideTooltip /> : null}</div>}
            key={TabKeys.Clinical}
            forceRender
          >
            <div className={styles.comingSoonWrapper}>
              <div>
                <div>
                  <div className={styles.questionTextBold}>Want to ask a question? Book in a time below.</div>
                  <div className={styles.questionText}>You can provide us with any questions you have in advance and we will do our best to answer them during the call.</div>
                </div>
                <div className={styles.buttonContainer}>
                  {clinicalMeetingData.isInMeeting ?
                    <div>
                      <div className={styles.callDescription}><span className={styles.emphasis}>Clinical Support</span> session with Dr Hayley Watson is Live. Join Now!</div>
                      <div className={styles.joinBtnContainer}>
                        <Button className={styles.joinBtn} onClick={joinMeeting(clinicalMeetingUrl)} label={'Join Zoom Call'} />
                      </div>
                    </div>
                    : <>
                      <div className={styles.callDescription}>Next <span className={styles.emphasis}>Clinical Support</span> session with Dr Hayley Watson starting in:</div>
                        {clinicalMeetingData.timeLeftInSec === 0 ? <div className={styles.noMeetings}>There are no meetings scheduled at this stage.</div>
                          : <CountDown timeInSec={clinicalMeetingData.timeLeftInSec || 0} onCountDownHitZero={onCountDownHitZero} />}
                    </>
                  }
                </div>
              </div>
              {isLoading ? (
                <div className={styles.spinnerContainer}>
                  <div className={styles.spinner}>
                    <Spinner />
                  </div>
                </div>
              ) : null}
              <InlineWidget
                url={Calendar_Hayley_URL}
                iframeTitle="Click here to schedule!"
                styles={{ height: '460px', minWidth: '320px', width: '100%', overflow: 'hidden' }}
                prefill={prefill}
                pageSettings={pageSettings}
                utm={utm}
              />
              {/*<div className={styles.descriptionTextsContainer}>*/}
              {/*  <div className={styles.questionTextBold}>Don’t have a question, but still want to attend? </div>*/}
              {/*  <div className={styles.descriptionSmallText}>You can attend any Live session by <span style={{ fontWeight: 700 }}>clicking the Join link</span> when it’s active.</div>*/}
              {/*  <div className={styles.descriptionSmallText}>Use this option if you would like to observe and listen to the questions of others. If you would like to ask a question, we will do our best to answer it during the call.</div>*/}
              {/*</div>*/}
            </div>
          </TabPane>
          <TabPane
            tab={<div className={`${styles.tabTitle} ${activeTab === TabKeys.Onboarding ? styles.activeTab : ''}`}>Onboarding{onboardingMeetingData.isInMeeting ? <PulseDot hideTooltip /> : null}</div>}
            key={TabKeys.Onboarding}
            forceRender
          >
            <div className={styles.comingSoonWrapper}>
              <div>
                <div>
                  <div className={styles.questionTextBold}>New to Open Parachute?</div>
                  <div className={styles.questionText}>Join this session to learn how to use the platform, where to find the programs, and all the features that are included, such as parent resources, exit tickets, surveys, the Help Centre and more!</div>
                </div>
                <div className={styles.buttonContainer}>
                  {onboardingMeetingData.isInMeeting ?
                    <div>
                      <div className={styles.callDescription}><span className={styles.emphasis}>Onboarding</span> session with <br />Tom Pastro is Live. Join Now!</div>
                      <div className={styles.joinBtnContainer}>
                        <Button className={styles.joinBtn} onClick={joinMeeting(onboardingMeetingUrl)} label={'Join Zoom Call'} />
                      </div>
                    </div>
                    : <>
                      <div className={styles.callDescription}>Next <span className={styles.emphasis}>Onboarding</span> session with Tom Pastro starting in:</div>
                      {onboardingMeetingData.timeLeftInSec === 0 ? <div className={styles.noMeetings}>There are no meetings scheduled at this stage.</div>
                      : <CountDown timeInSec={onboardingMeetingData.timeLeftInSec || 0} onCountDownHitZero={onCountDownHitZero} />}
                    </>
                  }
                </div>
              </div>
              {isLoading ? (
                <div className={styles.spinnerContainer}>
                  <div className={styles.spinner}>
                    <Spinner />
                  </div>
                </div>
              ) : null}
              <InlineWidget
                url={Calendar_URL}
                iframeTitle="Click here to schedule!"
                styles={{ height: '460px', minWidth: '320px', overflow: 'hidden' }}
                prefill={prefill}
                pageSettings={pageSettings}
                utm={utm}
              />
            </div>
          </TabPane>
        </Tabs>
      </div>
      <div>
        <div className={styles.descriptionTextsContainer}>
          <div className={styles.questionTextBold}>Don’t have a question, but still want to attend? </div>
          <div className={styles.descriptionSmallText}>You can attend any Live session by <span style={{ fontWeight: 700 }}>clicking the Join link</span> when it’s active.</div>
          <div className={styles.descriptionSmallText}>Use this option if you would like to observe and listen to the questions of others. If you would like to ask a question, we will do our best to answer it during the call.</div>
        </div>
      </div>
    </Modal>
  );
}

export default OfficeHourModal;
